import React, { useEffect, useMemo, useState } from "react";
import { api, inr, formatApiError, fmtDate } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import FileAttachment from "@/components/FileAttachment";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter
} from "@/components/ui/dialog";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue
} from "@/components/ui/select";
import { toast } from "sonner";
import { useAuth } from "@/contexts/AuthContext";
import PageExportBar from "@/components/PageExportBar";
import { openPrintStatement } from "@/lib/printStatement";
import {
  Vault, Bank, ArrowsLeftRight, Plus, Trash,
  ArrowDownLeft, ArrowUpRight, ArrowsClockwise, Printer
} from "@phosphor-icons/react";

const today = () => new Date().toISOString().slice(0, 10);
const startOfMonth = () => {
  const d = new Date();
  return new Date(d.getFullYear(), d.getMonth(), 1).toISOString().slice(0, 10);
};

const Kpi = ({ label, value, icon: Icon, color, foot, testid }) => (
  <div className="kpi" data-testid={testid}>
    <div className="eyebrow flex items-center gap-1.5">
      {Icon ? <Icon size={12} /> : null} {label}
    </div>
    <div className="val" style={color ? { color } : undefined}>{value}</div>
    {foot ? <div className="text-[11px] font-mono mt-1 text-muted-foreground">{foot}</div> : null}
  </div>
);

function DepositDialog({ kind, onSaved }) {
  const { dateFloor } = useAuth();
  const [open, setOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({ date: today(), amount: 0, reference: "", note: "", attachment_data_url: "" });

  const submit = async (e) => {
    e.preventDefault();
    if (!form.date) { toast.error("Date is mandatory"); return; }
    if (!isDeposit && !form.reference.trim()) { toast.error("Reference / Cheque Number is mandatory"); return; }
    if (!form.attachment_data_url) { toast.error(`Please attach the ${kind === "deposit" ? "deposit slip / receipt" : "receipt / cheque"} — it is mandatory`); return; }
    setSaving(true);
    try {
      await api.post("/cashbook/deposits", { ...form, amount: Number(form.amount) || 0, kind });
      toast.success(kind === "deposit" ? "Cash deposit recorded" : "Cash withdrawal recorded");
      setOpen(false);
      setForm({ date: today(), amount: 0, reference: "", note: "", attachment_data_url: "" });
      onSaved && onSaved();
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setSaving(false); }
  };

  const isDeposit = kind === "deposit";
  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button className="h-9" variant={isDeposit ? "default" : "outline"} data-testid={`new-${kind}-btn`}>
          {isDeposit ? <ArrowUpRight size={14} className="mr-1.5" /> : <ArrowDownLeft size={14} className="mr-1.5" />}
          {isDeposit ? "Deposit Cash to Bank" : "Withdraw Cash from Bank"}
        </Button>
      </DialogTrigger>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle className="font-heading tracking-tight">
            {isDeposit ? "Deposit Cash to Bank" : "Withdraw Cash from Bank"}
          </DialogTitle>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-4">
          <div className="text-[12px] text-muted-foreground">
            {isDeposit
              ? "Cash-in-hand decreases and bank balance increases by this amount."
              : "Bank balance decreases and cash-in-hand increases by this amount."}
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <Label className="eyebrow">Date *</Label>
              <Input type="date" value={form.date} min={dateFloor} required
                onChange={(e) => setForm({ ...form, date: e.target.value })} className="font-mono" data-testid={`${kind}-date`} />
            </div>
            <div className="space-y-1.5">
              <Label className="eyebrow">Amount *</Label>
              <Input type="number" step="0.01" value={form.amount} required
                onChange={(e) => setForm({ ...form, amount: e.target.value })} className="font-mono" data-testid={`${kind}-amount`} autoFocus />
            </div>
          </div>
          <div className="space-y-1.5">
            <Label className="eyebrow">{isDeposit ? "Reference" : "Reference / Cheque Number *"}</Label>
            <Input value={form.reference}
              onChange={(e) => setForm({ ...form, reference: e.target.value })}
              placeholder={isDeposit ? "Slip #, txn id" : "Cheque #, txn id"} className="font-mono" data-testid={`${kind}-reference`} />
          </div>
          <div className="space-y-1.5">
            <Label className="eyebrow">Note</Label>
            <Textarea value={form.note} rows={2}
              onChange={(e) => setForm({ ...form, note: e.target.value })} />
          </div>
          <FileAttachment
            label={isDeposit ? "Attach Deposit Slip / Receipt *" : "Attach Receipt / Cheque *"}
            value={form.attachment_data_url}
            onChange={(v) => setForm({ ...form, attachment_data_url: v })}
            testid={`${kind}-attachment`}
          />
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
            <Button type="submit" disabled={saving} data-testid={`${kind}-save-btn`}>
              {saving ? "Saving…" : (isDeposit ? "Deposit" : "Withdraw")}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

export default function CashBook() {
  const { activeCompanyId, activeCompany, activeStoreId, dateFloor } = useAuth();
  const [summary, setSummary] = useState(null);
  const [rows, setRows] = useState([]);
  const [bucket, setBucket] = useState("all");
  const [start, setStart] = useState(startOfMonth());
  const [end, setEnd] = useState(today());
  const [refreshKey, setRefreshKey] = useState(0);

  const load = async () => {
    if (!activeCompanyId) return;
    try {
      const [s, l] = await Promise.all([
        api.get(`/cashbook/summary?start=${start}&end=${end}`),
        api.get(`/cashbook/ledger?bucket=${bucket}&start=${start}&end=${end}`),
      ]);
      setSummary(s.data);
      setRows(l.data.rows || []);
    } catch (err) { toast.error(formatApiError(err)); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [activeCompanyId, activeStoreId, bucket, start, end, refreshKey]);

  const bal = summary?.balances || { cash: 0, bank: 0, upi: 0, total: 0 };
  const totals = summary?.totals || {};

  const filteredRows = useMemo(() => rows.slice().reverse(), [rows]);

  const exportColumns = [
    { key: "date", label: "Date" },
    { key: "kind", label: "Kind" },
    { key: "voucher_type", label: "Type" },
    { key: "number", label: "Voucher #" },
    { key: "bucket", label: "Bucket" },
    { key: "party_name", label: "Party / Description" },
    { key: "mode", label: "Mode" },
    { key: "reference", label: "Reference" },
    { key: "direction", label: "Direction" },
    { key: "amount", label: "Amount" },
  ];

  const deleteDeposit = async (id) => {
    if (!window.confirm("Delete this deposit/withdrawal? It will reverse the balance change.")) return;
    try {
      await api.delete(`/cashbook/deposits/${id}`);
      toast.success("Deleted");
      setRefreshKey((k) => k + 1);
    } catch (err) { toast.error(formatApiError(err)); }
  };

  const printStatement = () => {
    if (!filteredRows.length) { toast.error("No cash-book entries in the current range."); return; }
    // Group rows by bucket (cash / upi / bank) + a transfers section
    const groups = { cash: [], bank: [], transfer: [] };
    filteredRows.forEach((r) => {
      const key = r.kind === "transfer" ? "transfer" : (groups[r.bucket] ? r.bucket : "cash");
      groups[key].push(r);
    });
    const label = {
      cash: "Cash Movements",
      bank: "Bank Movements",
      transfer: "Cash ⇄ Bank Transfers",
    };
    const cols = [
      { key: "date", label: "Date", mono: true },
      { key: "number", label: "Voucher #", mono: true },
      { key: "party", label: "Party / Description" },
      { key: "mode", label: "Mode" },
      { key: "reference", label: "Reference", mono: true },
      { key: "in", label: "Money In", align: "right", mono: true, tone: "pos" },
      { key: "out", label: "Money Out", align: "right", mono: true, tone: "neg" },
    ];
    const sections = Object.keys(groups).filter((k) => groups[k].length).map((k) => {
      const list = groups[k];
      const inSum = list.reduce((s, r) => s + (r.direction === "in" ? Number(r.amount || 0) : 0), 0);
      const outSum = list.reduce((s, r) => s + (r.direction === "out" ? Number(r.amount || 0) : 0), 0);
      return {
        heading: label[k] || k.toUpperCase(),
        subheading: `${list.length} entry(ies) · In <b>${inr(inSum)}</b> · Out <b>${inr(outSum)}</b>`,
        columns: cols,
        rows: list.map((r) => ({
          date: r.date,
          number: r.number || (r.kind === "transfer" ? (r.voucher_type === "deposit" ? "DEP" : "WDR") : "—"),
          party: r.party_name || (r.kind === "transfer" ? (r.voucher_type === "deposit" ? "Cash → Bank" : "Bank → Cash") : ""),
          mode: (r.mode || "").toUpperCase() || "—",
          reference: r.reference || "—",
          in: r.direction === "in" ? inr(r.amount) : (r.kind === "transfer" && r.voucher_type === "withdraw" ? inr(r.amount) : ""),
          out: r.direction === "out" ? inr(r.amount) : (r.kind === "transfer" && r.voucher_type === "deposit" ? inr(r.amount) : ""),
        })),
      };
    });
    const res = openPrintStatement({
      title: `Cash Book Statement (${start} → ${end})`,
      company: activeCompany || {},
      sections,
      totals: [
        { label: "Cash in Hand", value: inr(bal.cash), tone: bal.cash < 0 ? "neg" : "pos" },
        { label: "Bank Balance", value: inr(bal.bank), tone: bal.bank < 0 ? "neg" : "" },
        { label: "Total Liquidity", value: inr(bal.total) },
        { label: "Cash Received", value: inr(totals.cash_in || 0), tone: "pos" },
        { label: "Cash Paid", value: inr(totals.cash_out || 0), tone: "neg" },
      ],
      note: bucket === "all"
        ? "Cash Book — all buckets. Balances shown are live (as of today)."
        : `Cash Book — filtered to '${bucket.toUpperCase()}' bucket. Balances shown are live (as of today).`,
    });
    if (!res.ok) toast.error(res.error);
    else toast.success(`Preparing statement for ${filteredRows.length} entry(ies)…`);
  };

  if (!summary) return <div className="page-shell text-sm text-muted-foreground">Loading cash & bank…</div>;

  return (
    <div className="page-shell space-y-6" id="report-print-root">
      <div className="flex items-center justify-between flex-wrap gap-4">
        <div>
          <div className="eyebrow flex items-center gap-1.5"><Vault size={11} /> Cash & Bank</div>
          <h1 className="font-heading text-3xl font-bold tracking-tight mt-1">Cash Book</h1>
          <p className="text-sm text-muted-foreground mt-1">Live cash-in-hand and bank balances (UPI receipts/payments count in Bank). Deposits from cash to bank auto-balance both.</p>
        </div>
        <div className="flex items-center gap-2 flex-wrap no-print">
          <DepositDialog kind="deposit" onSaved={() => setRefreshKey((k) => k + 1)} />
          <DepositDialog kind="withdraw" onSaved={() => setRefreshKey((k) => k + 1)} />
          <Button variant="outline" className="h-9" onClick={printStatement} data-testid="cashbook-print-statement">
            <Printer size={14} className="mr-1.5" /> Print Statement
          </Button>
          <PageExportBar
            title="Cash Book"
            filename={`cashbook-${today()}`}
            columns={exportColumns}
            rows={filteredRows}
            testidPrefix="cashbook"
          />
        </div>
      </div>

      {/* Live balances */}
      <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
        <Kpi label="Cash in Hand" value={inr(bal.cash)} icon={Vault} color={bal.cash < 0 ? "hsl(var(--destructive))" : "hsl(var(--success))"} testid="cashbook-kpi-cash" />
        <Kpi label="Bank Balance" value={inr(bal.bank)} icon={Bank} color={bal.bank < 0 ? "hsl(var(--destructive))" : undefined} testid="cashbook-kpi-bank" />
        <Kpi label="Total" value={inr(bal.total)} icon={ArrowsLeftRight} testid="cashbook-kpi-total" />
      </div>

      {/* Period filter + period totals */}
      <div className="card-flat p-4 flex flex-wrap items-end gap-3 no-print">
        <div className="space-y-1.5">
          <Label className="eyebrow">From</Label>
          <Input type="date" min="1900-01-01" value={start} onChange={(e) => setStart(e.target.value)} className="font-mono h-9 w-40" data-testid="cashbook-start" />
        </div>
        <div className="space-y-1.5">
          <Label className="eyebrow">To</Label>
          <Input type="date" min="1900-01-01" value={end} onChange={(e) => setEnd(e.target.value)} className="font-mono h-9 w-40" data-testid="cashbook-end" />
        </div>
        <Button variant="outline" onClick={() => setRefreshKey((k) => k + 1)} className="h-9" data-testid="cashbook-refresh">
          <ArrowsClockwise size={13} className="mr-1.5" /> Refresh
        </Button>
        <div className="ml-auto text-[11px] text-muted-foreground">Balances shown are as of TODAY; the period below controls tabs & totals.</div>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
        <Kpi label="Cash Received" value={inr(totals.cash_in || 0)} color="hsl(var(--success))" testid="cashbook-cash-in" />
        <Kpi label="Cash Paid Out" value={inr(totals.cash_out || 0)} color="hsl(var(--destructive))" testid="cashbook-cash-out" />
        <Kpi label="Bank Received (incl. UPI)" value={inr(totals.bank_in || 0)} color="hsl(var(--success))" testid="cashbook-bank-in" />
        <Kpi label="Bank Paid (incl. UPI)" value={inr(totals.bank_out || 0)} color="hsl(var(--destructive))" testid="cashbook-bank-out" />
        <Kpi label="Cash → Bank" value={inr(totals.deposited_to_bank || 0)} testid="cashbook-deposited" />
        <Kpi label="Bank → Cash" value={inr(totals.withdrawn_from_bank || 0)} testid="cashbook-withdrawn" />
      </div>

      <Tabs value={bucket} onValueChange={setBucket}>
        <TabsList data-testid="cashbook-tabs">
          <TabsTrigger value="all" data-testid="cashbook-tab-all">All</TabsTrigger>
          <TabsTrigger value="cash" data-testid="cashbook-tab-cash">Cash</TabsTrigger>
          <TabsTrigger value="bank" data-testid="cashbook-tab-bank">Bank</TabsTrigger>
          <TabsTrigger value="deposit" data-testid="cashbook-tab-deposits">Cash ⇄ Bank</TabsTrigger>
        </TabsList>
        <TabsContent value={bucket} className="mt-4">
          <div className="card-flat overflow-hidden">
            <table className="data-table" data-testid="cashbook-table">
              <thead>
                <tr>
                  <th>Date</th>
                  <th>Voucher #</th>
                  <th>Party / Description</th>
                  <th>Mode</th>
                  <th>Ref</th>
                  <th className="num">Money In</th>
                  <th className="num">Money Out</th>
                  <th className="num" style={{ textAlign: "right" }}></th>
                </tr>
              </thead>
              <tbody>
                {filteredRows.length === 0 ? (
                  <tr><td colSpan={8} className="text-center text-muted-foreground py-6">No movements in range.</td></tr>
                ) : filteredRows.map((r) => (
                  <tr key={`${r.kind}-${r.id}`}>
                    <td className="font-mono">{fmtDate(r.date)}</td>
                    <td className="font-mono">{r.number || (r.kind === "transfer" ? "" : "—")}</td>
                    <td>
                      {r.party_name}
                      <div className="text-[10.5px] uppercase tracking-wider text-muted-foreground mt-0.5">
                        {r.kind === "transfer"
                          ? (r.voucher_type === "deposit" ? "Cash → Bank" : "Bank → Cash")
                          : `${r.bucket} · ${r.voucher_type}`}
                      </div>
                    </td>
                    <td className="text-[12px]">{r.mode || "—"}</td>
                    <td className="font-mono text-[12px]">{r.reference || "—"}</td>
                    <td className="num font-mono" style={{ color: "hsl(var(--success))" }}>
                      {r.direction === "in" ? inr(r.amount) : (r.kind === "transfer" && r.voucher_type === "withdraw" ? inr(r.amount) : "")}
                    </td>
                    <td className="num font-mono" style={{ color: "hsl(var(--destructive))" }}>
                      {r.direction === "out" ? inr(r.amount) : (r.kind === "transfer" && r.voucher_type === "deposit" ? inr(r.amount) : "")}
                    </td>
                    <td className="num" style={{ textAlign: "right" }}>
                      {r.kind === "transfer" && (
                        <Button variant="ghost" size="sm" className="h-8" onClick={() => deleteDeposit(r.id)} data-testid={`cashbook-del-${r.id}`}>
                          <Trash size={12} />
                        </Button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </TabsContent>
      </Tabs>
    </div>
  );
}
